import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Container } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Flag, CheckCircle2, Loader2, AlertCircle } from "lucide-react";

const REASONS = [
  { v: "possible_fraud", label: "Possible fraud" },
  { v: "impersonation", label: "Impersonation" },
  { v: "misleading", label: "Misleading information" },
  { v: "duplicate", label: "Duplicate campaign" },
  { v: "payout_concern", label: "Payout concern" },
];

export default function ReportCampaign() {
  const [params] = useSearchParams();
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState(params.get("campaign") || "");
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [reporterEmail, setReporterEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/campaigns", { params: { status: "live", limit: 100 } })
      .then((r) => setCampaigns(r.data)).catch(() => setCampaigns([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!campaignId) return setError("Please select a campaign.");
    if (!reason) return setError("Please choose a reason.");
    setLoading(true);
    try {
      await api.post("/complaints", {
        campaign_id: campaignId, reason, description,
        reporter_email: reporterEmail || undefined,
      });
      setDone(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#F3F4F6]">
      <Container className="flex min-h-[70vh] items-center justify-center py-14">
        <div className="w-full max-w-lg rounded-2xl border border-ff-border bg-white p-8 shadow-sm">
          {done ? (
            <div className="py-8 text-center" data-testid="report-success">
              <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-ff-green" />
              <h1 className="font-heading text-2xl font-bold text-ff-blue">Report received</h1>
              <p className="mt-2 text-sm text-ff-dark">Our Trust &amp; Safety team will review this campaign. Thank you for helping keep FightFund safe.</p>
              <Button asChild className="mt-6 bg-ff-blue text-white hover:bg-ff-blue/90"><Link to="/browse">Back to browse</Link></Button>
            </div>
          ) : (
            <>
              <div className="mb-1 flex items-center gap-2 text-ff-red"><Flag className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-wider">Report a campaign</span></div>
              <h1 className="font-heading text-2xl font-bold text-ff-blue">Tell us what's wrong</h1>
              <p className="mt-2 text-sm text-ff-dark">Reports are confidential and reviewed by our team.</p>

              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="report-form">
                {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
                <div>
                  <Label>Campaign</Label>
                  <Select value={campaignId} onValueChange={setCampaignId}>
                    <SelectTrigger className="mt-1.5" data-testid="report-campaign-select"><SelectValue placeholder="Select a campaign" /></SelectTrigger>
                    <SelectContent>
                      {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reason</Label>
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger className="mt-1.5" data-testid="report-reason-select"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
                    <SelectContent>
                      {REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="desc">Details</Label>
                  <Textarea id="desc" rows={4} data-testid="report-description" value={description}
                    onChange={(e) => setDescription(e.target.value)} className="mt-1.5" placeholder="Describe your concern…" />
                </div>
                <div>
                  <Label htmlFor="remail">Your email (optional)</Label>
                  <Input id="remail" type="email" data-testid="report-email" value={reporterEmail}
                    onChange={(e) => setReporterEmail(e.target.value)} className="mt-1.5" />
                </div>
                <Button type="submit" disabled={loading} data-testid="report-submit-button"
                  className="w-full bg-ff-red font-semibold text-white hover:bg-ff-red/90">
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting</> : "Submit Report"}
                </Button>
              </form>
            </>
          )}
        </div>
      </Container>
    </div>
  );
}
